// src/components/Games/OPUVeronica.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './OPUVeronica.css';

// ═══════════════════════════════════════════════════════════════════════════════
// DRONE GAME CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════
const CANVAS_WIDTH         = 800;
const CANVAS_HEIGHT        = 500;
const DRONE_SIZE           = 24;
const MISSILE_SPEED        = 9;
const MISSILE_SIZE         = 4;
const TARGET_BASE_SPEED    = 1.2;
const SPAWN_INTERVAL_START = 1800;
const SPAWN_INTERVAL_MIN   = 700;
const GAME_DURATION_SEC    = 60;
const PARTICLE_COUNT       = 16;
const STAR_COUNT           = 80;

// CHANGED: Added 3 new enemy types — UFO, STEALTH, TANK
const TARGET_TYPES = {
  STANDARD: { color: '#ff4444', glowColor: 'rgba(255,68,68,0.6)',    points: 1,  speed: 1,   size: 20, label: 'HOSTILE',  hp: 1 },
  ARMORED:  { color: '#ffaa00', glowColor: 'rgba(255,170,0,0.6)',    points: 2,  speed: 0.7, size: 26, label: 'ARMORED',  hp: 3 },
  FAST:     { color: '#ff44ff', glowColor: 'rgba(255,68,255,0.6)',   points: 2,  speed: 2.2, size: 16, label: 'EVASIVE',  hp: 1 },
  DECOY:    { color: '#4488ff', glowColor: 'rgba(68,136,255,0.6)',   points: -2, speed: 0.9, size: 18, label: 'CIVILIAN', hp: 1 },
  BOMBER:   { color: '#ff2200', glowColor: 'rgba(255,34,0,0.8)',     points: 3,  speed: 0.5, size: 30, label: 'BOMBER',   hp: 5 },
  SWARM:    { color: '#ff6666', glowColor: 'rgba(255,102,102,0.5)',  points: 1,  speed: 1.8, size: 12, label: 'SWARM',    hp: 1 },
  // CHANGED: UFO — zigzag movement, high points, conspiracy-themed
  UFO:      { color: '#00ff88', glowColor: 'rgba(0,255,136,0.7)',    points: 4,  speed: 1.5, size: 22, label: 'UAP',      hp: 2 },
  // CHANGED: STEALTH — flickers in/out of visibility
  STEALTH:  { color: '#8844ff', glowColor: 'rgba(136,68,255,0.6)',   points: 3,  speed: 1.3, size: 18, label: 'STEALTH',  hp: 2 },
  // CHANGED: TANK — slow but very tough
  TANK:     { color: '#888888', glowColor: 'rgba(180,180,180,0.6)',  points: 5,  speed: 0.4, size: 32, label: 'TANK',     hp: 8 },
};

const POWERUP_TYPES = {
  RAPID:  { color: '#00ffff', glowColor: 'rgba(0,255,255,0.6)',   label: 'RAPID FIRE',   duration: 5000, symbol: '»' },
  TRIPLE: { color: '#ffff00', glowColor: 'rgba(255,255,0,0.6)',   label: 'TRIPLE SHOT',  duration: 6000, symbol: '⋔' },
  NUKE:   { color: '#ff0088', glowColor: 'rgba(255,0,136,0.8)',   label: 'SCREEN CLEAR', duration: 0,    symbol: '✸' },
  FREEZE: { color: '#88ccff', glowColor: 'rgba(136,204,255,0.6)', label: 'TIME FREEZE',  duration: 3000, symbol: '◈' },
};

function getDifficultyTier(confirmedKills) {
  const tier = Math.min(Math.floor((confirmedKills || 0) / 100), 10);
  return {
    tier,
    label:                    getDifficultyLabel(tier),
    enemySpeedMultiplier:     1 + tier * 0.12,
    enemyHpMultiplier:        1 + tier * 0.15,
    enemySizeReduction:       1 - tier * 0.02,
    spawnRateMultiplier:      1 + tier * 0.08,
    homingStrengthMultiplier: 1 + tier * 0.1,
    decoyFrequency:           Math.min(0.10 + tier * 0.03, 0.35),
    droneSpeedMultiplier:     1 - tier * 0.02,
    missileSpeedMultiplier:   1 - tier * 0.015,
    shotCooldownMultiplier:   1 + tier * 0.05,
    powerupSpawnChance:       1 - tier * 0.05,
    comboDecayFaster:         1 - tier * 0.08,
    swarmCountMultiplier:     1 + tier * 0.15,
    droneHitRadius:           20 + tier * 2,
    droneDamageEnabled:       tier >= 2,
    droneMaxHp:               Math.max(5 - Math.floor(tier / 2), 2),
  };
}

function getDifficultyLabel(tier) {
  if (tier === 0) return { text: 'ROOKIE',    color: '#00ff88' };
  if (tier <= 1)  return { text: 'SEASONED',  color: '#88ff00' };
  if (tier <= 2)  return { text: 'VETERAN',   color: '#ffff00' };
  if (tier <= 3)  return { text: 'HARDENED',  color: '#ffaa00' };
  if (tier <= 5)  return { text: 'LETHAL',    color: '#ff4400' };
  if (tier <= 7)  return { text: 'NIGHTMARE', color: '#ff0044' };
  return                  { text: 'HELL',     color: '#ff00ff' };
}

function generateStars() {
  return Array.from({ length: STAR_COUNT }, () => ({
    x:          Math.random() * CANVAS_WIDTH,
    y:          Math.random() * CANVAS_HEIGHT,
    size:       Math.random() * 1.5 + 0.5,
    speed:      Math.random() * 0.3 + 0.1,
    brightness: Math.random() * 0.5 + 0.3,
  }));
}

function generateTerrain() {
  const points = [];
  for (let x = 0; x <= CANVAS_WIDTH + 100; x += 20) {
    points.push({
      x,
      y: CANVAS_HEIGHT - 30 + Math.sin(x * 0.02) * 15 + Math.random() * 8,
    });
  }
  return points;
}

// CHANGED: conspiracy-themed background symbols (eye-of-providence, pyramid, redacted blocks)
function generateConspiracySymbols() {
  const symbols = [];
  for (let i = 0; i < 6; i++) {
    symbols.push({
      x:        Math.random() * CANVAS_WIDTH,
      y:        Math.random() * CANVAS_HEIGHT,
      type:     ['pyramid', 'eye', 'redacted', 'sigil'][Math.floor(Math.random() * 4)],
      size:     30 + Math.random() * 50,
      rotation: Math.random() * Math.PI * 2,
      drift:    (Math.random() - 0.5) * 0.1,
      opacity:  0.03 + Math.random() * 0.05,
      phase:    Math.random() * Math.PI * 2,
    });
  }
  return symbols;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DRONE GAME COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function DroneGame({ missionId, missionTitle, onComplete, onCancel, confirmedKills = 0 }) {
  const difficulty = useRef(getDifficultyTier(confirmedKills)).current;

  const [gameState,        setGameState]        = useState('BRIEFING');
  const [score,            setScore]            = useState(0);
  const [timeLeft,         setTimeLeft]         = useState(GAME_DURATION_SEC);
  const [targetsDestroyed, setTargetsDestroyed] = useState(0);
  const [shotsFired,       setShotsFired]       = useState(0);
  const [civilianHits,     setCivilianHits]     = useState(0);
  const [comboCount,       setComboCount]       = useState(0);
  const [maxCombo,         setMaxCombo]         = useState(0);
  const [feedMessages,     setFeedMessages]     = useState([]);
  const [activePowerup,    setActivePowerup]    = useState(null);
  const [waveAlert,        setWaveAlert]        = useState(null);
  const [droneHp,          setDroneHp]          = useState(difficulty.droneMaxHp);

  const canvasRef        = useRef(null);
  const gameLoopRef      = useRef(null);
  const spawnTimerRef    = useRef(null);
  const countdownRef     = useRef(null);
  const droneRef         = useRef({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
  const missilesRef      = useRef([]);
  const targetsRef       = useRef([]);
  const particlesRef     = useRef([]);
  const powerupsRef      = useRef([]);
  const starsRef         = useRef(generateStars());
  const terrainOffsetRef = useRef(0);
  const terrainRef       = useRef(generateTerrain());
  const symbolsRef       = useRef(generateConspiracySymbols()); // CHANGED
  const keysRef          = useRef({});
  const scoreRef         = useRef(0);
  const comboRef         = useRef(0);
  const maxComboRef      = useRef(0);
  const destroyedRef     = useRef(0);
  const firedRef         = useRef(0);
  const civilianRef      = useRef(0);
  const feedRef          = useRef([]);
  const mouseRef         = useRef({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
  const lastShotRef      = useRef(0);
  const gameTimeRef      = useRef(0);
  const shakeRef         = useRef({ x: 0, y: 0, intensity: 0 });
  const activePowerupRef = useRef(null);
  const powerupTimerRef  = useRef(null);
  const freezeActiveRef  = useRef(false);
  const lastKillTimeRef  = useRef(0);
  const waveTriggeredRef = useRef({});
  const floatingTextsRef = useRef([]);
  const droneHpRef       = useRef(difficulty.droneMaxHp);
  const droneInvulnRef   = useRef(0);
  const gameEndedRef     = useRef(false);
  const frameCountRef    = useRef(0); // CHANGED: for stealth flicker timing

  const SHOT_COOLDOWN  = 150;
  const RAPID_COOLDOWN = 60;
  const COMBO_DECAY_MS = 3000;

   // CHANGED: optional priority filter — drop low-priority msgs if a recent high-priority one exists
  const addFeed = useCallback((text, type = 'info') => {
    const PRIORITY = { mega: 5, nuke: 5, combo: 4, warning: 4, kill: 3, powerup: 3, system: 2, hit: 1, info: 1 };
    const last = feedRef.current[0];
    // Skip low-priority message if a higher-priority one is less than 400ms old
    if (last && (PRIORITY[type] || 1) < (PRIORITY[last.type] || 1) && Date.now() - last.time < 400) {
      return;
    }
    const msg = { id: Date.now() + Math.random(), text, type, time: Date.now() };
    feedRef.current = [msg, ...feedRef.current].slice(0, 1);
    setFeedMessages([...feedRef.current]);
  }, []);

  const addFloatingText = useCallback((x, y, text, color) => {
    floatingTextsRef.current.push({ x, y, text, color, life: 1.0, vy: -1.5 });
  }, []);

  const triggerShake = useCallback((intensity) => {
    shakeRef.current.intensity = Math.min(intensity, 15);
  }, []);

  const spawnExplosion = useCallback((x, y, color, large = false) => {
    const count = large ? PARTICLE_COUNT * 3 : PARTICLE_COUNT;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = (large ? 2.5 : 1.5) + Math.random() * (large ? 5 : 3);
      particlesRef.current.push({
        x, y,
        vx:    Math.cos(angle) * speed,
        vy:    Math.sin(angle) * speed,
        life:  1.0,
        decay: 0.012 + Math.random() * 0.02,
        size:  (large ? 3 : 2) + Math.random() * (large ? 5 : 3),
        color,
      });
    }
  }, []);

  const endGame = useCallback(() => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;
    setGameState('ENDED');
    cancelAnimationFrame(gameLoopRef.current);
    clearTimeout(spawnTimerRef.current);
    clearInterval(countdownRef.current);
    if (powerupTimerRef.current) clearTimeout(powerupTimerRef.current);
  }, []);

  const spawnTarget = useCallback((forceType = null) => {
    const elapsed = gameTimeRef.current;
    let type;
    if (forceType) {
      type = forceType;
    } else {
      const roll        = Math.random();
      const decoyChance = difficulty.decoyFrequency;
      // CHANGED: expanded spawn pool with new enemy types
      if      (elapsed > 45 && roll < 0.06)           type = TARGET_TYPES.TANK;
      else if (elapsed > 40 && roll < 0.10)           type = TARGET_TYPES.BOMBER;
      else if (elapsed > 30 && roll < 0.14)           type = TARGET_TYPES.UFO;
      else if (elapsed > 25 && roll < 0.20)           type = TARGET_TYPES.STEALTH;
      else if (elapsed > 20 && roll < 0.26)           type = TARGET_TYPES.BOMBER;
      else if (roll < decoyChance)                    type = TARGET_TYPES.DECOY;
      else if (roll < decoyChance + 0.15)             type = TARGET_TYPES.ARMORED;
      else if (roll < decoyChance + 0.32)             type = TARGET_TYPES.FAST;
      else                                            type = TARGET_TYPES.STANDARD;
    }

    const side           = Math.floor(Math.random() * 4);
    const speed          = TARGET_BASE_SPEED * type.speed * (0.8 + Math.random() * 0.4)
                           * difficulty.enemySpeedMultiplier;
    const timeMultiplier = 1 + (elapsed / GAME_DURATION_SEC) * 0.4;
    let x, y, vx, vy;

    switch (side) {
      case 0:
        x = Math.random() * CANVAS_WIDTH; y = -type.size;
        vx = (Math.random() - 0.5) * speed * 2 * timeMultiplier;
        vy = speed * timeMultiplier; break;
      case 1:
        x = CANVAS_WIDTH + type.size; y = Math.random() * CANVAS_HEIGHT * 0.7;
        vx = -speed * timeMultiplier;
        vy = (Math.random() - 0.5) * speed * timeMultiplier; break;
      case 2:
        x = Math.random() * CANVAS_WIDTH; y = CANVAS_HEIGHT + type.size;
        vx = (Math.random() - 0.5) * speed * 2 * timeMultiplier;
        vy = -speed * timeMultiplier; break;
      default:
        x = -type.size; y = Math.random() * CANVAS_HEIGHT * 0.7;
        vx = speed * timeMultiplier;
        vy = (Math.random() - 0.5) * speed * timeMultiplier; break;
    }

    const scaledHp   = Math.ceil(type.hp * difficulty.enemyHpMultiplier);
    const scaledSize = Math.max(8, Math.round(type.size * difficulty.enemySizeReduction));

    targetsRef.current.push({
      id: Date.now() + Math.random(),
      x, y, vx, vy, type,
      hp: scaledHp, maxHp: scaledHp,
      effectiveSize: scaledSize,
      flashTimer: 0, age: 0,
      evasionPhase: type === TARGET_TYPES.FAST ? Math.random() * Math.PI * 2 : 0,
      // CHANGED: state for new enemy types
      zigzagPhase: type === TARGET_TYPES.UFO ? Math.random() * Math.PI * 2 : 0,
      stealthPhase: type === TARGET_TYPES.STEALTH ? Math.random() * Math.PI * 2 : 0,
      baseVx: vx, baseVy: vy,
    });
  }, [difficulty]);

  const spawnSwarmWave = useCallback((count) => {
    setWaveAlert('⚠ SWARM INCOMING ⚠');
    setTimeout(() => setWaveAlert(null), 2000);
    addFeed('⚠ HOSTILE SWARM DETECTED — MULTIPLE CONTACTS', 'warning');
    triggerShake(4);
    for (let i = 0; i < count; i++) {
      setTimeout(() => spawnTarget(TARGET_TYPES.SWARM), i * 150);
    }
  }, [spawnTarget, addFeed, triggerShake]);

  const spawnPowerup = useCallback(() => {
    const types = Object.values(POWERUP_TYPES);
    const type  = types[Math.floor(Math.random() * types.length)];
    powerupsRef.current.push({
      id: Date.now() + Math.random(),
      x:  100 + Math.random() * (CANVAS_WIDTH - 200),
      y:  80  + Math.random() * (CANVAS_HEIGHT - 200),
      type, age: 0, maxAge: 480,
    });
    addFeed(`SUPPLY DROP DETECTED — ${type.label}`, 'powerup');
  }, [addFeed]);

  const activatePowerup = useCallback((type) => {
    if (powerupTimerRef.current) clearTimeout(powerupTimerRef.current);
    if (type === POWERUP_TYPES.NUKE) {
      const killed = targetsRef.current.filter(t => t.type !== TARGET_TYPES.DECOY).length;
      targetsRef.current.forEach((t) => {
        if (t.type !== TARGET_TYPES.DECOY) {
          spawnExplosion(t.x, t.y, t.type.color);
          scoreRef.current += t.type.points;
          destroyedRef.current++;
          addFloatingText(t.x, t.y, `+${t.type.points}`, t.type.color);
        }
      });
      targetsRef.current = targetsRef.current.filter(t => t.type === TARGET_TYPES.DECOY);
      setScore(scoreRef.current);
      setTargetsDestroyed(destroyedRef.current);
      triggerShake(12);
      addFeed(`✸ ORBITAL STRIKE — ${killed} TARGETS ELIMINATED`, 'nuke');
      setActivePowerup({ type, expiry: Date.now() + 500 });
      setTimeout(() => setActivePowerup(null), 500);
      return;
    }
    if (type === POWERUP_TYPES.FREEZE) {
      freezeActiveRef.current = true;
      addFeed('◈ TEMPORAL FREEZE — TARGETS HALTED', 'powerup');
    }
    activePowerupRef.current = type;
    setActivePowerup({ type, expiry: Date.now() + type.duration });
    powerupTimerRef.current = setTimeout(() => {
      activePowerupRef.current = null;
      freezeActiveRef.current  = false;
      setActivePowerup(null);
      addFeed('POWERUP EXPIRED', 'info');
    }, type.duration);
  }, [addFeed, triggerShake, addFloatingText, spawnExplosion]);

  const fireMissile = useCallback(() => {
    const now          = Date.now();
    const baseCooldown = activePowerupRef.current === POWERUP_TYPES.RAPID
      ? RAPID_COOLDOWN : SHOT_COOLDOWN;
    const cooldown = baseCooldown * difficulty.shotCooldownMultiplier;
    if (now - lastShotRef.current < cooldown) return;
    lastShotRef.current = now;

    const drone = droneRef.current;
    const mouse = mouseRef.current;
    const dx    = mouse.x - drone.x;
    const dy    = mouse.y - drone.y;
    const dist  = Math.sqrt(dx * dx + dy * dy) || 1;
    const scaledMissileSpeed = MISSILE_SPEED * difficulty.missileSpeedMultiplier;
    const createMissile = (angle) => ({
      x: drone.x, y: drone.y,
      vx: Math.cos(angle) * scaledMissileSpeed,
      vy: Math.sin(angle) * scaledMissileSpeed,
      trail: [],
    });
    const baseAngle = Math.atan2(dy / dist, dx / dist);

    if (activePowerupRef.current === POWERUP_TYPES.TRIPLE) {
      missilesRef.current.push(createMissile(baseAngle - 0.15));
      missilesRef.current.push(createMissile(baseAngle));
      missilesRef.current.push(createMissile(baseAngle + 0.15));
      firedRef.current += 3;
    } else {
      missilesRef.current.push(createMissile(baseAngle));
      firedRef.current++;
    }
    setShotsFired(firedRef.current);
  }, [difficulty]);

  const update = useCallback(() => {
    if (gameEndedRef.current) return;
    frameCountRef.current++; // CHANGED
    const drone       = droneRef.current;
    const keys        = keysRef.current;
    const DRONE_SPEED = 4 * difficulty.droneSpeedMultiplier;

    if (shakeRef.current.intensity > 0) {
      shakeRef.current.x         = (Math.random() - 0.5) * shakeRef.current.intensity * 2;
      shakeRef.current.y         = (Math.random() - 0.5) * shakeRef.current.intensity * 2;
      shakeRef.current.intensity *= 0.9;
      if (shakeRef.current.intensity < 0.3) {
        shakeRef.current.intensity = 0;
        shakeRef.current.x = 0;
        shakeRef.current.y = 0;
      }
    }

    const scaledComboDecay = COMBO_DECAY_MS * Math.max(difficulty.comboDecayFaster, 0.3);
    if (comboRef.current > 0 && Date.now() - lastKillTimeRef.current > scaledComboDecay) {
      comboRef.current = 0;
      setComboCount(0);
    }

    if (droneInvulnRef.current > 0) droneInvulnRef.current--;

    if (keys['w'] || keys['arrowup'])    drone.y -= DRONE_SPEED;
    if (keys['s'] || keys['arrowdown'])  drone.y += DRONE_SPEED;
    if (keys['a'] || keys['arrowleft'])  drone.x -= DRONE_SPEED;
    if (keys['d'] || keys['arrowright']) drone.x += DRONE_SPEED;
    drone.x = Math.max(DRONE_SIZE, Math.min(CANVAS_WIDTH  - DRONE_SIZE, drone.x));
    drone.y = Math.max(DRONE_SIZE, Math.min(CANVAS_HEIGHT - DRONE_SIZE, drone.y));

    missilesRef.current = missilesRef.current.filter((m) => {
      m.trail.push({ x: m.x, y: m.y });
      if (m.trail.length > 8) m.trail.shift();
      m.x += m.vx;
      m.y += m.vy;
      return m.x > -10 && m.x < CANVAS_WIDTH + 10 &&
             m.y > -10 && m.y < CANVAS_HEIGHT + 10;
    });

    // CHANGED: drift conspiracy symbols
    symbolsRef.current.forEach((s) => {
      s.rotation += s.drift * 0.01;
      s.phase    += 0.02;
    });

    const isFrozen = freezeActiveRef.current;
    targetsRef.current = targetsRef.current.filter((t) => {
      if (!isFrozen) {
        if (t.type === TARGET_TYPES.FAST) {
          t.evasionPhase += 0.08;
          const perpX    = -t.baseVy;
          const perpY    =  t.baseVx;
          const perpDist = Math.sqrt(perpX * perpX + perpY * perpY) || 1;
          const evasionAmp = 1.5 * difficulty.enemySpeedMultiplier;
          t.x += t.baseVx + (perpX / perpDist) * Math.sin(t.evasionPhase) * evasionAmp;
          t.y += t.baseVy + (perpY / perpDist) * Math.sin(t.evasionPhase) * evasionAmp;
        // CHANGED: UFO zigzag pattern
        } else if (t.type === TARGET_TYPES.UFO) {
          t.zigzagPhase += 0.12;
          const perpX    = -t.baseVy;
          const perpY    =  t.baseVx;
          const perpDist = Math.sqrt(perpX * perpX + perpY * perpY) || 1;
          t.x += t.baseVx + (perpX / perpDist) * Math.sin(t.zigzagPhase) * 3;
          t.y += t.baseVy + (perpY / perpDist) * Math.sin(t.zigzagPhase) * 3;
        // CHANGED: STEALTH ship moves normally but flickers visibility
        } else if (t.type === TARGET_TYPES.STEALTH) {
          t.stealthPhase += 0.06;
          t.x += t.vx;
          t.y += t.vy;
        } else {
          t.x += t.vx;
          t.y += t.vy;
        }
        t.age++;
        if (t.flashTimer > 0) t.flashTimer--;
        if (t.age > 120 && t.type !== TARGET_TYPES.DECOY && t.type !== TARGET_TYPES.SWARM) {
          const dx   = drone.x - t.x;
          const dy   = drone.y - t.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const baseHoming     = t.type === TARGET_TYPES.BOMBER ? 0.04
            : t.type === TARGET_TYPES.TANK ? 0.05 : 0.02; // CHANGED: TANK homes harder
          const homingStrength = baseHoming * difficulty.homingStrengthMultiplier;
          t.vx += (dx / dist) * homingStrength;
          t.vy += (dy / dist) * homingStrength;
        }
      } else {
        t.x += (Math.random() - 0.5) * 0.3;
        t.y += (Math.random() - 0.5) * 0.3;
        if (t.flashTimer > 0) t.flashTimer--;
      }
      return t.x > -80 && t.x < CANVAS_WIDTH + 80 &&
             t.y > -80 && t.y < CANVAS_HEIGHT + 80;
    });

    powerupsRef.current = powerupsRef.current.filter((p) => {
      p.age++;
      const dx = drone.x - p.x;
      const dy = drone.y - p.y;
      if (dx * dx + dy * dy < 900) {
        activatePowerup(p.type);
        spawnExplosion(p.x, p.y, p.type.color);
        triggerShake(3);
        return false;
      }
      return p.age < p.maxAge;
    });

    particlesRef.current = particlesRef.current.filter((p) => {
      p.x += p.vx; p.y += p.vy;
      p.vx *= 0.97; p.vy *= 0.97;
      p.life -= p.decay;
      return p.life > 0;
    });

    floatingTextsRef.current = floatingTextsRef.current.filter((ft) => {
      ft.y += ft.vy;
      ft.life -= 0.02;
      return ft.life > 0;
    });

    starsRef.current.forEach((s) => {
      s.x -= s.speed;
      if (s.x < 0) { s.x = CANVAS_WIDTH; s.y = Math.random() * CANVAS_HEIGHT; }
    });
    terrainOffsetRef.current += 0.5;

    // ── Missile vs target collisions ─────────────────────────────────────────
    const missilesToRemove = new Set();
    const targetsToRemove  = new Set();

    missilesRef.current.forEach((m, mi) => {
      targetsRef.current.forEach((t, ti) => {
        if (missilesToRemove.has(mi) || targetsToRemove.has(ti)) return;
        const dx      = m.x - t.x;
        const dy      = m.y - t.y;
        const hitDist = MISSILE_SIZE + (t.effectiveSize || t.type.size);
        if (dx * dx + dy * dy < hitDist * hitDist) {
          missilesToRemove.add(mi);
          t.hp--;
          t.flashTimer = 6;
          if (t.hp <= 0) {
            targetsToRemove.add(ti);
            const isLarge = t.type === TARGET_TYPES.BOMBER || t.type === TARGET_TYPES.TANK;
            spawnExplosion(t.x, t.y, t.type.color, isLarge);
            const pts = t.type.points;
            let comboBonus = 0;
            if (pts > 0) {
              comboRef.current++;
              lastKillTimeRef.current = Date.now();
              if (comboRef.current > maxComboRef.current) {
                maxComboRef.current = comboRef.current;
                setMaxCombo(maxComboRef.current);
              }
              setComboCount(comboRef.current);
              if      (comboRef.current >= 10) comboBonus = 3;
              else if (comboRef.current >= 5)  comboBonus = 1;
            }
            const totalPts = pts + comboBonus;
            scoreRef.current = Math.max(0, scoreRef.current + totalPts);
            setScore(scoreRef.current);
            if (pts > 0) {
              destroyedRef.current++;
              setTargetsDestroyed(destroyedRef.current);
              triggerShake(isLarge ? 8 : 2);
              const displayPts = comboBonus > 0 ? `+${pts}+${comboBonus}` : `+${pts}`;
              addFloatingText(t.x, t.y - 15, displayPts, t.type.color);
              if      (comboRef.current >= 10) addFeed(`★ MEGA COMBO x${comboRef.current} — ${t.type.label} OBLITERATED [+${totalPts}]`, 'mega');
              else if (comboRef.current >= 5)  addFeed(`COMBO x${comboRef.current} — ${t.type.label} NEUTRALIZED [+${totalPts}]`, 'combo');
              else                             addFeed(`${t.type.label} TARGET DESTROYED [+${pts}]`, 'kill');
            } else {
              civilianRef.current++;
              setCivilianHits(civilianRef.current);
              comboRef.current = 0;
              setComboCount(0);
              triggerShake(6);
              addFloatingText(t.x, t.y - 15, `${pts}`, '#ff4444');
              addFeed(`⚠ CIVILIAN ASSET DESTROYED [${pts}] — COLLATERAL LOGGED`, 'warning');
            }
            if (t.type === TARGET_TYPES.BOMBER) {
              targetsRef.current.forEach((nearby, ni) => {
                if (targetsToRemove.has(ni)) return;
                const ndx = nearby.x - t.x;
                const ndy = nearby.y - t.y;
                if (ndx * ndx + ndy * ndy < 80 * 80) {
                  nearby.hp -= 2;
                  nearby.flashTimer = 10;
                  if (nearby.hp <= 0) {
                    targetsToRemove.add(ni);
                    spawnExplosion(nearby.x, nearby.y, nearby.type.color);
                    if (nearby.type.points > 0) {
                      scoreRef.current += nearby.type.points;
                      destroyedRef.current++;
                      addFloatingText(nearby.x, nearby.y - 15, `+${nearby.type.points}`, '#ff8800');
                    }
                  }
                }
              });
              setScore(scoreRef.current);
              setTargetsDestroyed(destroyedRef.current);
            }
          } else {
            addFeed(`HIT — ${t.type.label} hull integrity ${t.hp}/${t.maxHp}`, 'hit');
            triggerShake(1);
          }
        }
      });
    });

    missilesRef.current = missilesRef.current.filter((_, i) => !missilesToRemove.has(i));
    targetsRef.current  = targetsRef.current.filter((_,  i) => !targetsToRemove.has(i));

    // ── Drone collision ───────────────────────────────────────────────────────
    if (difficulty.droneDamageEnabled && droneInvulnRef.current <= 0) {
      for (let i = 0; i < targetsRef.current.length; i++) {
        const t = targetsRef.current[i];
        if (t.type === TARGET_TYPES.DECOY) continue;
        const dx            = drone.x - t.x;
        const dy            = drone.y - t.y;
        const collisionDist = difficulty.droneHitRadius + (t.effectiveSize || t.type.size) * 0.5;
        if (dx * dx + dy * dy < collisionDist * collisionDist) {
          droneHpRef.current--;
          setDroneHp(droneHpRef.current);
          droneInvulnRef.current = 60;
          triggerShake(10);
          spawnExplosion(drone.x, drone.y, '#00ff88');
          addFloatingText(drone.x, drone.y - 20, 'HULL DAMAGE', '#ff0000');
          addFeed(`⚠ DRONE HULL BREACH — INTEGRITY ${droneHpRef.current}/${difficulty.droneMaxHp}`, 'warning');
          if (droneHpRef.current <= 0) {
            addFeed('✖ DRONE DESTROYED — MISSION FAILED', 'warning');
            spawnExplosion(drone.x, drone.y, '#00ff88', true);
            spawnExplosion(drone.x, drone.y, '#ff4444', true);
            endGame();
            return;
          }
          break;
        }
      }
    }
  }, [difficulty, spawnExplosion, addFeed, addFloatingText, triggerShake, activatePowerup, endGame]);

  // CHANGED: helper to draw conspiracy symbols on background
  const drawConspiracySymbol = useCallback((ctx, sym) => {
    ctx.save();
    ctx.translate(sym.x, sym.y);
    ctx.rotate(sym.rotation);
    const pulse = 0.7 + Math.sin(sym.phase) * 0.3;
    ctx.globalAlpha = sym.opacity * pulse;
    ctx.strokeStyle = '#00ff88';
    ctx.fillStyle   = '#00ff88';
    ctx.lineWidth   = 1;

    if (sym.type === 'pyramid') {
      // All-seeing eye pyramid
      const s = sym.size;
      ctx.beginPath();
      ctx.moveTo(0, -s / 2);
      ctx.lineTo(s / 2, s / 2);
      ctx.lineTo(-s / 2, s / 2);
      ctx.closePath();
      ctx.stroke();
      // Inner eye
      ctx.beginPath();
      ctx.arc(0, s / 6, s / 8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, s / 6, s / 24, 0, Math.PI * 2);
      ctx.fill();
    } else if (sym.type === 'eye') {
      const s = sym.size;
      ctx.beginPath();
      ctx.ellipse(0, 0, s / 2, s / 4, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, s / 6, 0, Math.PI * 2);
      ctx.stroke();
    } else if (sym.type === 'redacted') {
      // Redacted text bar
      ctx.fillStyle = '#000';
      ctx.globalAlpha = sym.opacity * 2 * pulse;
      ctx.fillRect(-sym.size / 2, -6, sym.size, 12);
      ctx.strokeStyle = '#ff4444';
      ctx.strokeRect(-sym.size / 2, -6, sym.size, 12);
      ctx.fillStyle   = '#ff4444';
      ctx.font        = 'bold 8px Courier New';
      ctx.textAlign   = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('[REDACTED]', 0, 0);
    } else if (sym.type === 'sigil') {
      // Pentagram-ish sigil
      const s = sym.size / 2;
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (Math.PI * 2 * i * 2) / 5 - Math.PI / 2;
        const x = Math.cos(a) * s;
        const y = Math.sin(a) * s;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, s, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx   = canvas.getContext('2d');
    const drone = droneRef.current;
    ctx.save();
    if (shakeRef.current.intensity > 0) ctx.translate(shakeRef.current.x, shakeRef.current.y);

    ctx.fillStyle = activePowerupRef.current === POWERUP_TYPES.NUKE ? '#221100' : '#050508';
    ctx.fillRect(-10, -10, CANVAS_WIDTH + 20, CANVAS_HEIGHT + 20);

    if (freezeActiveRef.current) {
      ctx.fillStyle = 'rgba(100,180,255,0.03)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }

    starsRef.current.forEach((s) => {
      ctx.fillStyle = freezeActiveRef.current
        ? `rgba(136,204,255,${s.brightness})`
        : `rgba(0,255,136,${s.brightness})`;
      ctx.fillRect(s.x, s.y, s.size, s.size);
    });

    // CHANGED: draw conspiracy symbols behind everything
    symbolsRef.current.forEach((sym) => drawConspiracySymbol(ctx, sym));

    const terrain = terrainRef.current;
    const offset  = terrainOffsetRef.current % 20;
    ctx.strokeStyle = 'rgba(0,255,136,0.12)';
    ctx.lineWidth   = 1;
    ctx.beginPath();
    terrain.forEach((p, i) => {
      const tx = p.x - offset;
      if (i === 0) ctx.moveTo(tx, p.y); else ctx.lineTo(tx, p.y);
    });
    ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.lineTo(0, CANVAS_HEIGHT);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0,255,136,0.03)';
    ctx.fill();
    ctx.stroke();

    ctx.strokeStyle = 'rgba(0,255,136,0.04)';
    ctx.lineWidth   = 0.5;
    for (let gx = 0; gx < CANVAS_WIDTH;  gx += 50) {
      ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, CANVAS_HEIGHT); ctx.stroke();
    }
    for (let gy = 0; gy < CANVAS_HEIGHT; gy += 50) {
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(CANVAS_WIDTH, gy); ctx.stroke();
    }

    powerupsRef.current.forEach((p) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      const pulse   = 0.6 + Math.sin(p.age * 0.08) * 0.4;
      const fadeOut = p.age > p.maxAge - 120 ? (p.maxAge - p.age) / 120 : 1;
      ctx.globalAlpha = fadeOut;
      ctx.shadowColor = p.type.glowColor;
      ctx.shadowBlur  = 15 * pulse;
      const rotation  = p.age * 0.03;
      ctx.rotate(rotation);
      ctx.fillStyle = p.type.color;
      ctx.beginPath();
      ctx.moveTo(0, -14); ctx.lineTo(14, 0); ctx.lineTo(0, 14); ctx.lineTo(-14, 0);
      ctx.closePath(); ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle  = '#050508';
      ctx.font       = 'bold 12px Courier New';
      ctx.textAlign  = 'center';
      ctx.textBaseline = 'middle';
      ctx.rotate(-rotation);
      ctx.fillText(p.type.symbol, 0, 1);
      ctx.restore();
    });

    particlesRef.current.forEach((p) => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle   = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    });
    ctx.globalAlpha = 1;

    targetsRef.current.forEach((t) => {
      // CHANGED: STEALTH ships have variable opacity
      let visibility = 1;
      if (t.type === TARGET_TYPES.STEALTH && !freezeActiveRef.current) {
        visibility = 0.25 + (Math.sin(t.stealthPhase) + 1) * 0.375; // 0.25 → 1.0
      }

      ctx.save();
      ctx.globalAlpha = visibility;
      ctx.translate(t.x, t.y);
      if (freezeActiveRef.current) {
        ctx.shadowColor = 'rgba(136,204,255,0.8)'; ctx.shadowBlur = 12;
      } else {
        ctx.shadowColor = t.flashTimer > 0 ? '#ffffff' : t.type.glowColor;
        ctx.shadowBlur  = t.flashTimer > 0 ? 20 : 10;
      }
      const s       = t.effectiveSize || t.type.size;
      ctx.fillStyle = t.flashTimer > 0 ? '#ffffff'
        : (freezeActiveRef.current ? '#88ccff' : t.type.color);

      if (t.type === TARGET_TYPES.BOMBER) {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI * 2 * i) / 6 - Math.PI / 6;
          const px = Math.cos(angle) * s; const py = Math.sin(angle) * s;
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath(); ctx.fill();
        ctx.shadowBlur = 0; ctx.fillStyle = '#050508';
        ctx.font = `bold ${s * 0.7}px Courier New`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('!', 0, 1);
      } else if (t.type === TARGET_TYPES.SWARM) {
        ctx.beginPath();
        ctx.moveTo(0, -s); ctx.lineTo(s * 0.6, s * 0.5); ctx.lineTo(-s * 0.6, s * 0.5);
        ctx.closePath(); ctx.fill();
      // CHANGED: UFO — classic saucer with dome and lights
      } else if (t.type === TARGET_TYPES.UFO) {
        // Saucer body
        ctx.beginPath();
        ctx.ellipse(0, 0, s, s * 0.35, 0, 0, Math.PI * 2);
        ctx.fill();
        // Dome on top
        ctx.beginPath();
        ctx.arc(0, -s * 0.15, s * 0.5, Math.PI, 0);
        ctx.fill();
        // Lights around the rim
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffff00';
        for (let i = 0; i < 5; i++) {
          const lx = -s * 0.7 + (i * s * 0.35);
          ctx.beginPath();
          ctx.arc(lx, s * 0.1, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      // CHANGED: STEALTH — dark angular triangle (F-117-ish)
      } else if (t.type === TARGET_TYPES.STEALTH) {
        ctx.beginPath();
        ctx.moveTo(0, -s);
        ctx.lineTo(s * 0.9, s * 0.7);
        ctx.lineTo(0, s * 0.3);
        ctx.lineTo(-s * 0.9, s * 0.7);
        ctx.closePath();
        ctx.fill();
        // Faceted highlight
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#bb88ff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, -s); ctx.lineTo(0, s * 0.3);
        ctx.stroke();
      // CHANGED: TANK — armored rectangular shape with treads
      } else if (t.type === TARGET_TYPES.TANK) {
        // Body
        ctx.fillRect(-s * 0.7, -s * 0.5, s * 1.4, s * 1.0);
        // Turret
        ctx.fillStyle = '#aaaaaa';
        ctx.fillRect(-s * 0.35, -s * 0.25, s * 0.7, s * 0.5);
        // Barrel
        ctx.fillRect(-s * 0.08, -s * 0.9, s * 0.16, s * 0.7);
        // Tread dots
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#444';
        for (let i = 0; i < 4; i++) {
          ctx.fillRect(-s * 0.7 + i * s * 0.4, -s * 0.55, s * 0.2, s * 0.08);
          ctx.fillRect(-s * 0.7 + i * s * 0.4,  s * 0.47, s * 0.2, s * 0.08);
        }
      } else {
        ctx.beginPath();
        ctx.moveTo(0, -s); ctx.lineTo(s * 0.7, 0);
        ctx.lineTo(0, s * 0.6); ctx.lineTo(-s * 0.7, 0);
        ctx.closePath(); ctx.fill();
      }

      if (t.maxHp > 1) {
        ctx.shadowBlur = 0;
        const barW = s * 1.4; const barH = 3;
        const barX = -barW / 2; const barY = -s - 8;
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(barX, barY, barW, barH);
        ctx.fillStyle = freezeActiveRef.current ? '#88ccff' : t.type.color;
        ctx.fillRect(barX, barY, barW * (t.hp / t.maxHp), barH);
      }
      ctx.restore();
    });

    missilesRef.current.forEach((m) => {
      m.trail.forEach((tp, i) => {
        const alpha = (i / m.trail.length) * 0.5;
        ctx.fillStyle = `rgba(0,255,136,${alpha})`;
        const ts = MISSILE_SIZE * (i / m.trail.length);
        ctx.fillRect(tp.x - ts / 2, tp.y - ts / 2, ts, ts);
      });
      ctx.shadowColor = '#00ff88'; ctx.shadowBlur = 8; ctx.fillStyle = '#00ff88';
      ctx.fillRect(m.x - MISSILE_SIZE / 2, m.y - MISSILE_SIZE / 2, MISSILE_SIZE, MISSILE_SIZE);
      ctx.shadowBlur = 0;
    });

    ctx.save();
    ctx.translate(drone.x, drone.y);
    const mouse   = mouseRef.current;
    const aimDx   = mouse.x - drone.x;
    const aimDy   = mouse.y - drone.y;
    const aimDist = Math.sqrt(aimDx * aimDx + aimDy * aimDy) || 1;
    ctx.strokeStyle = 'rgba(0,255,136,0.15)';
    ctx.lineWidth   = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo((aimDx / aimDist) * 60, (aimDy / aimDist) * 60);
    ctx.stroke();
    ctx.setLineDash([]);

    const isInvuln  = droneInvulnRef.current > 0;
    const showDrone = !isInvuln || Math.floor(droneInvulnRef.current / 4) % 2 === 0;
    if (showDrone) {
      ctx.shadowColor = activePowerupRef.current ? activePowerupRef.current.color : '#00ff88';
      ctx.shadowBlur  = 15;
      ctx.fillStyle   = activePowerupRef.current ? activePowerupRef.current.color : '#00ff88';
      ctx.beginPath();
      ctx.moveTo(0, -DRONE_SIZE * 0.6);
      ctx.lineTo( DRONE_SIZE * 0.5, -DRONE_SIZE * 0.2);
      ctx.lineTo( DRONE_SIZE * 0.5,  DRONE_SIZE * 0.2);
      ctx.lineTo(0,  DRONE_SIZE * 0.4);
      ctx.lineTo(-DRONE_SIZE * 0.5,  DRONE_SIZE * 0.2);
      ctx.lineTo(-DRONE_SIZE * 0.5, -DRONE_SIZE * 0.2);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#050508';
      ctx.beginPath(); ctx.arc(0, 0, DRONE_SIZE * 0.15, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = activePowerupRef.current ? activePowerupRef.current.color : '#00ff88';
      ctx.lineWidth   = 2;
      ctx.beginPath(); ctx.moveTo(-DRONE_SIZE * 0.5, 0); ctx.lineTo(-DRONE_SIZE, -DRONE_SIZE * 0.3); ctx.stroke();
      ctx.beginPath(); ctx.moveTo( DRONE_SIZE * 0.5, 0); ctx.lineTo( DRONE_SIZE, -DRONE_SIZE * 0.3); ctx.stroke();
    }
    if (isInvuln) {
      ctx.shadowBlur  = 0;
      ctx.strokeStyle = `rgba(255,255,255,${0.3 + Math.sin(droneInvulnRef.current * 0.5) * 0.2})`;
      ctx.lineWidth   = 2;
      ctx.beginPath(); ctx.arc(0, 0, DRONE_SIZE + 6, 0, Math.PI * 2); ctx.stroke();
    }
    if (difficulty.droneDamageEnabled) {
      ctx.shadowBlur = 0;
      const hpBarW = 40; const hpBarH = 4;
      const hpBarX = -hpBarW / 2; const hpBarY = -DRONE_SIZE - 14;
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fillRect(hpBarX, hpBarY, hpBarW, hpBarH);
      const hpRatio = droneHpRef.current / difficulty.droneMaxHp;
      ctx.fillStyle = hpRatio > 0.6 ? '#00ff88' : hpRatio > 0.3 ? '#ffaa00' : '#ff4444';
      ctx.fillRect(hpBarX, hpBarY, hpBarW * Math.max(hpRatio, 0), hpBarH);
    }
    ctx.shadowBlur = 0;
    ctx.restore();

    ctx.strokeStyle = 'rgba(0,255,136,0.4)';
    ctx.lineWidth   = 1;
    const cx = mouse.x; const cy = mouse.y;
    ctx.beginPath(); ctx.arc(cx, cy, 12, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 18, cy); ctx.lineTo(cx - 8, cy);
    ctx.moveTo(cx + 8,  cy); ctx.lineTo(cx + 18, cy);
    ctx.moveTo(cx, cy - 18); ctx.lineTo(cx, cy - 8);
    ctx.moveTo(cx, cy + 8);  ctx.lineTo(cx, cy + 18);
    ctx.stroke();

    floatingTextsRef.current.forEach((ft) => {
      ctx.globalAlpha = ft.life;
      ctx.fillStyle   = ft.color;
      ctx.font        = 'bold 14px Courier New';
      ctx.textAlign   = 'center';
      ctx.fillText(ft.text, ft.x, ft.y);
    });
    ctx.globalAlpha = 1;

    ctx.fillStyle = 'rgba(0,0,0,0.03)';
    for (let sy = 0; sy < CANVAS_HEIGHT; sy += 3) ctx.fillRect(0, sy, CANVAS_WIDTH, 1);

    // CHANGED: occasional glitch line for conspiracy/surveillance aesthetic
    if (Math.random() < 0.015) {
      const glitchY = Math.random() * CANVAS_HEIGHT;
      ctx.fillStyle = `rgba(0,255,136,${0.1 + Math.random() * 0.15})`;
      ctx.fillRect(0, glitchY, CANVAS_WIDTH, 1 + Math.random() * 3);
    }

    const gradient = ctx.createRadialGradient(
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_HEIGHT * 0.4,
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_HEIGHT * 0.8
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.3)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    if (difficulty.tier > 0) {
      ctx.globalAlpha  = 0.06;
      ctx.fillStyle    = difficulty.label.color;
      ctx.font         = 'bold 60px Courier New';
      ctx.textAlign    = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`T${difficulty.tier}`, CANVAS_WIDTH - 15, CANVAS_HEIGHT - 10);
      ctx.globalAlpha  = 1;
    }
    ctx.restore();
  }, [difficulty, drawConspiracySymbol]);

  const gameLoop = useCallback(() => {
    update();
    render();
    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [update, render]);

  const startGame = useCallback(() => {
    scoreRef.current         = 0;
    comboRef.current         = 0;
    maxComboRef.current      = 0;
    destroyedRef.current     = 0;
    firedRef.current         = 0;
    civilianRef.current      = 0;
    feedRef.current          = [];
    gameTimeRef.current      = 0;
    waveTriggeredRef.current = {};
    activePowerupRef.current = null;
    freezeActiveRef.current  = false;
    gameEndedRef.current     = false;
    droneHpRef.current       = difficulty.droneMaxHp;
    droneInvulnRef.current   = 0;

    setScore(0); setTimeLeft(GAME_DURATION_SEC); setTargetsDestroyed(0);
    setShotsFired(0); setCivilianHits(0); setComboCount(0); setMaxCombo(0);
    setFeedMessages([]); setActivePowerup(null); setWaveAlert(null);
    setDroneHp(difficulty.droneMaxHp);

    droneRef.current         = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
    missilesRef.current      = [];
    targetsRef.current       = [];
    particlesRef.current     = [];
    powerupsRef.current      = [];
    floatingTextsRef.current = [];
    starsRef.current         = generateStars();
    symbolsRef.current       = generateConspiracySymbols(); // CHANGED

    setGameState('PLAYING');
    if (difficulty.tier > 0) {
      addFeed(`⚠ THREAT ASSESSMENT: ${difficulty.label.text} (TIER ${difficulty.tier})`, 'warning');
    }
    addFeed('DRONE SYSTEMS ONLINE — WEAPONS HOT', 'system');
    addFeed(`MISSION: ${missionId} — COMMENCE STRIKE OPERATIONS`, 'system');
    if (difficulty.droneDamageEnabled) {
      addFeed(`HULL INTEGRITY: ${difficulty.droneMaxHp} — AVOID HOSTILE CONTACT`, 'warning');
    }

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    const spawnTick = () => {
      if (gameTimeRef.current < GAME_DURATION_SEC && !gameEndedRef.current) {
        spawnTarget();
        const progress     = gameTimeRef.current / GAME_DURATION_SEC;
        const baseInterval = SPAWN_INTERVAL_START -
          (SPAWN_INTERVAL_START - SPAWN_INTERVAL_MIN) * progress;
        spawnTimerRef.current = setTimeout(
          spawnTick, baseInterval / difficulty.spawnRateMultiplier
        );
      }
    };
    spawnTimerRef.current = setTimeout(
      spawnTick, SPAWN_INTERVAL_START / difficulty.spawnRateMultiplier
    );

    let remaining = GAME_DURATION_SEC;
    countdownRef.current = setInterval(() => {
      if (gameEndedRef.current) { clearInterval(countdownRef.current); return; }
      remaining--;
      gameTimeRef.current = GAME_DURATION_SEC - remaining;
      setTimeLeft(remaining);
      const elapsed = GAME_DURATION_SEC - remaining;

      if (elapsed === 15 && !waveTriggeredRef.current[15]) {
        waveTriggeredRef.current[15] = true;
        spawnSwarmWave(Math.round(8 * difficulty.swarmCountMultiplier));
      }
      if (elapsed === 20 && !waveTriggeredRef.current[20]) {
        waveTriggeredRef.current[20] = true;
        if (Math.random() < difficulty.powerupSpawnChance) spawnPowerup();
      }
      if (elapsed === 30 && !waveTriggeredRef.current[30]) {
        waveTriggeredRef.current[30] = true;
        setWaveAlert('⚠ HEAVY CONTACTS INBOUND ⚠');
        setTimeout(() => setWaveAlert(null), 2000);
        addFeed('⚠ BOMBER SQUADRON DETECTED', 'warning');
        const bomberCount = 3 + Math.floor(difficulty.tier / 2);
        for (let i = 0; i < bomberCount; i++) {
          setTimeout(() => spawnTarget(TARGET_TYPES.BOMBER), i * 500);
        }
      }
      if (elapsed === 35 && !waveTriggeredRef.current[35]) {
        waveTriggeredRef.current[35] = true;
        if (Math.random() < difficulty.powerupSpawnChance) spawnPowerup();
      }
      if (elapsed === 45 && !waveTriggeredRef.current[45]) {
        waveTriggeredRef.current[45] = true;
        spawnSwarmWave(Math.round(14 * difficulty.swarmCountMultiplier));
      }
      if (elapsed === 48 && !waveTriggeredRef.current[48]) {
        waveTriggeredRef.current[48] = true;
        if (Math.random() < difficulty.powerupSpawnChance) spawnPowerup();
      }
      if (elapsed === 52 && !waveTriggeredRef.current[52]) {
        waveTriggeredRef.current[52] = true;
        setWaveAlert('⚠ FINAL ASSAULT WAVE ⚠');
        setTimeout(() => setWaveAlert(null), 2000);
        addFeed('⚠ ALL HOSTILES CONVERGING — HOLD THE LINE', 'warning');
        const finalCount = 5 + difficulty.tier;
        for (let i = 0; i < finalCount; i++) setTimeout(() => spawnTarget(), i * 300);
        setTimeout(() => spawnSwarmWave(Math.round(6 * difficulty.swarmCountMultiplier)), 1500);
      }
      if (remaining <= 10 && remaining > 0) addFeed(`⚠ ${remaining} SECONDS REMAINING`, 'warning');
      if (remaining <= 0) endGame();
    }, 1000);
  }, [difficulty, gameLoop, spawnTarget, spawnSwarmWave, spawnPowerup, addFeed, missionId, endGame]);

  // CHANGED: Input handlers — auto-start on mount and block ALL browser scroll keys
  useEffect(() => {
    // Auto-start game (no briefing screen)
    if (gameState === 'BRIEFING') {
      startGame();
    }
  }, [gameState, startGame]);

  // CHANGED: comprehensive scroll-blocking keys
  useEffect(() => {
    // Keys that should never propagate to the browser during play
    const BLOCKED_KEYS = new Set([
      ' ', 'spacebar',
      'arrowup', 'arrowdown', 'arrowleft', 'arrowright',
      'w', 'a', 's', 'd',
      'pageup', 'pagedown', 'home', 'end',
    ]);

    const handleKeyDown = (e) => {
      const k = e.key.toLowerCase();
      keysRef.current[k] = true;
      // Block browser default scroll behavior during gameplay
      if (gameState === 'PLAYING' && BLOCKED_KEYS.has(k)) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === ' ' && gameState === 'PLAYING') {
        fireMissile();
      }
    };

    const handleKeyUp = (e) => {
      const k = e.key.toLowerCase();
      keysRef.current[k] = false;
      if (gameState === 'PLAYING' && BLOCKED_KEYS.has(k)) {
        e.preventDefault();
      }
    };

    // CHANGED: block wheel/touch scroll during gameplay
    const handleWheel = (e) => {
      if (gameState === 'PLAYING') e.preventDefault();
    };
    const handleTouchMove = (e) => {
      if (gameState === 'PLAYING') e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('keyup',   handleKeyUp,   { passive: false });
    window.addEventListener('wheel',   handleWheel,   { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup',   handleKeyUp);
      window.removeEventListener('wheel',   handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [gameState, fireMissile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      const rect   = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH  / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;
      mouseRef.current = {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top)  * scaleY,
      };
    };
    const handleClick = (e) => {
      if (gameState === 'PLAYING') { e.preventDefault(); fireMissile(); }
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click',     handleClick);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click',     handleClick);
    };
  }, [gameState, fireMissile]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(gameLoopRef.current);
      clearTimeout(spawnTimerRef.current);
      clearInterval(countdownRef.current);
      if (powerupTimerRef.current) clearTimeout(powerupTimerRef.current);
    };
  }, []);

  // CHANGED: more aggressive scroll lock with html element too
  useEffect(() => {
    if (gameState !== 'PLAYING') return;

    const original = {
      bodyOverflow: document.body.style.overflow,
      bodyHeight:   document.body.style.height,
      htmlOverflow: document.documentElement.style.overflow,
      htmlHeight:   document.documentElement.style.height,
      scrollTop:    window.scrollY,
    };

    document.body.style.overflow         = 'hidden';
    document.body.style.height           = '100vh';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height   = '100vh';
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflow         = original.bodyOverflow;
      document.body.style.height           = original.bodyHeight;
      document.documentElement.style.overflow = original.htmlOverflow;
      document.documentElement.style.height   = original.htmlHeight;
      window.scrollTo(0, original.scrollTop);
    };
  }, [gameState]);

  // ── GAME OVER / DEBRIEF ───────────────────────────────────────────────────
  if (gameState === 'ENDED') {
    const accuracy       = firedRef.current > 0
      ? Math.round((destroyedRef.current / firedRef.current) * 100) : 0;
    const droneDestroyed = difficulty.droneDamageEnabled && droneHpRef.current <= 0;
    const rating =
      droneDestroyed         ? 'DRONE LOST'     :
      scoreRef.current >= 50 ? 'LEGENDARY'      :
      scoreRef.current >= 35 ? 'EXCEPTIONAL'    :
      scoreRef.current >= 25 ? 'COMMENDABLE'    :
      scoreRef.current >= 15 ? 'SATISFACTORY'   :
      scoreRef.current >= 8  ? 'MARGINAL'       : 'UNSATISFACTORY';
    const ratingColor =
      droneDestroyed         ? '#ff4444' :
      scoreRef.current >= 50 ? '#ff00ff' :
      scoreRef.current >= 35 ? '#00ff88' :
      scoreRef.current >= 25 ? '#00cc66' :
      scoreRef.current >= 15 ? '#ffaa00' :
      scoreRef.current >= 8  ? '#ff8844' : '#ff4444';

    return (
      <div className="drone-game-container">
        <div className="drone-debrief">
          <div className="drone-debrief-header">MISSION DEBRIEF</div>
          <div className="drone-debrief-divider" />
          <div className="drone-debrief-rating" style={{ color: ratingColor }}>{rating}</div>
          {difficulty.tier > 0 && (
            <div className="drone-debrief-difficulty" style={{ color: difficulty.label.color }}>
              {difficulty.label.text} DIFFICULTY — TIER {difficulty.tier}
            </div>
          )}
          <div className="drone-debrief-stats">
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value" style={{ color: '#00ff88' }}>
                {scoreRef.current}
              </span>
              <span className="drone-debrief-stat-label">FINAL SCORE</span>
            </div>
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value">{destroyedRef.current}</span>
              <span className="drone-debrief-stat-label">TARGETS NEUTRALIZED</span>
            </div>
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value">{accuracy}%</span>
              <span className="drone-debrief-stat-label">ACCURACY</span>
            </div>
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value">{maxComboRef.current}x</span>
              <span className="drone-debrief-stat-label">MAX COMBO</span>
            </div>
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value"
                    style={{ color: civilianRef.current > 0 ? '#ff4444' : '#00ff88' }}>
                {civilianRef.current}
              </span>
              <span className="drone-debrief-stat-label">CIVILIAN CASUALTIES</span>
            </div>
            <div className="drone-debrief-stat">
              <span className="drone-debrief-stat-value">{firedRef.current}</span>
              <span className="drone-debrief-stat-label">MISSILES FIRED</span>
            </div>
            {difficulty.droneDamageEnabled && (
              <div className="drone-debrief-stat">
                <span className="drone-debrief-stat-value"
                      style={{ color: droneDestroyed ? '#ff4444' : '#00ff88' }}>
                  {droneDestroyed ? 'DESTROYED' : `${droneHpRef.current}/${difficulty.droneMaxHp}`}
                </span>
                <span className="drone-debrief-stat-label">DRONE STATUS</span>
              </div>
            )}
          </div>
          <div className="drone-debrief-divider" />
          <div className="drone-debrief-actions">
            <button className="drone-btn-primary" onClick={() => onComplete(scoreRef.current)}>
              LOG SCORE & RETURN TO PORTAL
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── BRIEFING (shows briefly while game initializes) ────────────────────────
  if (gameState === 'BRIEFING') {
    return (
      <div className="drone-game-container">
        <div className="drone-briefing">
          <div className="drone-briefing-header">
            <span className="drone-briefing-flash">▶</span> INITIALIZING STRIKE PACKAGE...
          </div>
        </div>
      </div>
    );
  }

  // ── ACTIVE GAME ─────────────────────────────────────────────────────────────
  return (
    <div className="drone-game-container">
      {waveAlert && <div className="drone-wave-alert">{waveAlert}</div>}
      {activePowerup && activePowerup.type.duration > 0 && (
        <div className="drone-powerup-active" style={{ borderColor: activePowerup.type.color }}>
          <span style={{ color: activePowerup.type.color }}>{activePowerup.type.symbol}</span>
          {' '}{activePowerup.type.label}
        </div>
      )}
      <div className="drone-hud-top">
        <div className="drone-hud-item">
          <span className="drone-hud-label">TIME LEFT TO SURVIVE: </span>
          <span className={`drone-hud-value ${timeLeft <= 10 ? 'drone-hud-critical' : ''}`}>
            {timeLeft}s
          </span>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="drone-canvas"
        tabIndex={0}
      />
      <div className="drone-feed">
        {feedMessages.map((msg) => (
          <div key={msg.id} className={`drone-feed-msg drone-feed-${msg.type}`}>
            {msg.text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// OPU-VERONICA CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
// CHANGED: 30 → 20 levels
const TOTAL_LEVELS       = 20;
const MESSAGES_PER_LEVEL = 5;
const STORAGE_KEY        = 'opuweb_progress';

// CHANGED: removed hardcoded fallback; fail fast with a clear error
const VERONICA_API = process.env.REACT_APP_VERONICA_CHAT_API;

if (!VERONICA_API) {
  // Surfaces immediately during dev instead of silently 404-ing later
  console.error(
    '[OPUVeronica] Missing REACT_APP_VERONICA_CHAT_API environment variable. ' +
    'Add it to .env.development or .env.production and restart the dev server.'
  );
}

// CHANGED: replaced sectors with real cities grouped by country.
// Country order: United States → Europe → Russia → China → Pakistan (last).
// 20 levels total: 4 US + 4 EU + 4 RU + 4 CN + 4 PK
const LEVEL_DATA = [
  // United States (1-4)
  { id: 'OPU-NODE-01', name: 'WASHINGTON D.C. UPLINK',  region: 'UNITED STATES' },
  { id: 'OPU-NODE-02', name: 'NEW YORK CITY GRID',      region: 'UNITED STATES' },
  { id: 'OPU-NODE-03', name: 'DENVER RELAY',            region: 'UNITED STATES' },
  { id: 'OPU-NODE-04', name: 'LOS ANGELES CLUSTER',     region: 'UNITED STATES' },
  // Europe (5-8)
  { id: 'OPU-NODE-05', name: 'LONDON BACKBONE',         region: 'EUROPE'        },
  { id: 'OPU-NODE-06', name: 'PARIS EXCHANGE',          region: 'EUROPE'        },
  { id: 'OPU-NODE-07', name: 'BERLIN DATA HUB',         region: 'EUROPE'        },
  { id: 'OPU-NODE-08', name: 'ROME UPLINK',             region: 'EUROPE'        },
  // Russia (9-12)
  { id: 'OPU-NODE-09', name: 'MOSCOW INTERCEPT',        region: 'RUSSIA'        },
  { id: 'OPU-NODE-10', name: 'ST. PETERSBURG GRID',     region: 'RUSSIA'        },
  { id: 'OPU-NODE-11', name: 'NOVOSIBIRSK NODE',        region: 'RUSSIA'        },
  { id: 'OPU-NODE-12', name: 'YEKATERINBURG RELAY',     region: 'RUSSIA'        },
  // China (13-16)
  { id: 'OPU-NODE-13', name: 'BEIJING FIREWALL',        region: 'CHINA'         },
  { id: 'OPU-NODE-14', name: 'SHANGHAI EXCHANGE',       region: 'CHINA'         },
  { id: 'OPU-NODE-15', name: 'SHENZHEN ARRAY',          region: 'CHINA'         },
  { id: 'OPU-NODE-16', name: 'CHONGQING CLUSTER',       region: 'CHINA'         },
  // Pakistan (17-20) — final approach
  { id: 'OPU-NODE-17', name: 'KARACHI PERIMETER',       region: 'PAKISTAN'      },
  { id: 'OPU-NODE-18', name: 'LAHORE BREACH',           region: 'PAKISTAN'      },
  { id: 'OPU-NODE-19', name: 'ISLAMABAD CORE',          region: 'PAKISTAN'      },
  { id: 'OPU-NODE-20', name: 'PESHAWAR',     region: 'PAKISTAN'      },
];

const FALLBACK_RESPONSES = [
  '*Static crackles.* "Still here. Say that again?"',
  '*The feed cuts.* "Lost you there. Bad commection."',
  "*Veronica's voice fragments.* \"Something's messing up the signal. Try again.\"",
  '*Long pause, then:* "I\'m here. Just had to disconnect for a second. What did you say?"',
];

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveProgress(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {}
}

function defaultProgress() {
  return {
    currentLevel: 1, completedLevels: [],
    totalScore: 0, confirmedKills: 0, postgameUnlocked: false,
  };
}

// ─── Network map node ─────────────────────────────────────────────────────────
function NetworkNode({ levelNum, status, isCurrent, onClick }) {
  const level       = LEVEL_DATA[levelNum - 1];
  const color       = status === 'complete' ? '#00ff41' : status === 'available' ? '#ffaa00' : '#1a1a1a';
  const borderColor = status === 'complete' ? '#00ff41' : status === 'available' ? '#ffaa00' : '#333';
  return (
    <button
      className={`opuw-node ${status} ${isCurrent ? 'opuw-node-current' : ''}`}
      style={{ borderColor, color }}
      onClick={() => status !== 'locked' && onClick(levelNum)}
      disabled={status === 'locked'}
      title={`${level.id} — ${level.name}`}
    >
      <span className="opuw-node-num">{levelNum}</span>
      {status === 'complete' && <span className="opuw-node-check">✓</span>}
      {isCurrent && status === 'available' && <span className="opuw-node-pulse" />}
    </button>
  );
}

// ─── Veronica chat ────────────────────────────────────────────────────────────
function VeronicaChat({ level, mode, messagesUsed, onMessageSent, sessionId }) {
  const [input,       setInput]       = useState('');
  const [messages,    setMessages]    = useState([]);
  const [isLoading,   setIsLoading]   = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages]);

  const remainingMessages = mode === 'postgame' ? 999 : MESSAGES_PER_LEVEL - messagesUsed;
  const canSend = !isLoading && input.trim().length > 0 &&
    (mode === 'postgame' || remainingMessages > 0) && !rateLimited;

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setMessages(prev => [...prev, { role: 'player', text, id: Date.now() }]);
    setInput('');
    setIsLoading(true);
    setRateLimited(false);
    const history = messages.slice(-20).map(m => ({ role: m.role, text: m.text }));
    try {
      const res  = await fetch(VERONICA_API, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ message: text, sessionId, history, level, mode, messagesUsed }),
      });
      const data = await res.json();
      if (data.rateLimited) setRateLimited(true);
      setMessages(prev => [...prev, {
        role: 'veronica',
        text: data.response || FALLBACK_RESPONSES[0],
        id:   Date.now() + 1,
      }]);
      onMessageSent();
    } catch {
      setMessages(prev => [...prev, {
        role: 'veronica',
        text: FALLBACK_RESPONSES[Math.floor(Math.random() * FALLBACK_RESPONSES.length)],
        id:   Date.now() + 1,
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, level, mode, messagesUsed, sessionId, onMessageSent]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="opuw-chat">
      <div className="opuw-chat-header">
        <span className="opuw-chat-callsign">▶ VERONICA RODRIGUEZ</span>
        <span className="opuw-chat-status">
          {mode === 'postgame' ? 'OPEN CHANNEL' : `${remainingMessages} MSG REMAINING`}
        </span>
      </div>
      <div className="opuw-chat-feed" ref={feedRef}>
        {messages.length === 0 && (
          <div className="opuw-chat-empty">
            {mode === 'postgame' ? "— Channel open. She's been waiting. —" : '— Establish contact —'}
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`opuw-chat-msg opuw-chat-msg-${msg.role}`}>
            <span className="opuw-chat-msg-label">{msg.role === 'player' ? 'YOU' : 'VERONICA'}</span>
            <span className="opuw-chat-msg-text">{msg.text}</span>
          </div>
        ))}
        {isLoading && (
          <div className="opuw-chat-msg opuw-chat-msg-veronica">
            <span className="opuw-chat-msg-label">VERONICA</span>
            <span className="opuw-chat-typing"><span /><span /><span /></span>
          </div>
        )}
        {rateLimited && (
          <div className="opuw-chat-ratelimit">
            ⚠ Signal throttled — wait a moment before sending more messages.
          </div>
        )}
      </div>
      {(mode === 'postgame' || remainingMessages > 0) && (
        <div className="opuw-chat-input-row">
          <input
            className="opuw-chat-input" type="text" value={input}
            onChange={e => setInput(e.target.value.slice(0, 300))}
            onKeyDown={handleKeyDown}
            placeholder={mode === 'postgame' ? 'Say anything...' : 'Send a message to Veronica...'}
            disabled={isLoading} maxLength={300}
          />
          <button className="opuw-chat-send" onClick={sendMessage} disabled={!canSend}>
            SEND
          </button>
        </div>
      )}
      {mode !== 'postgame' && remainingMessages <= 0 && (
        <div className="opuw-chat-exhausted">
          ▓ MESSAGE LIMIT HIT — COMPLETE THIS CALIBRATION NODE TO RESTORE COMMS ▓
        </div>
      )}
    </div>
  );
}

// ─── Level complete ───────────────────────────────────────────────────────────
function LevelComplete({ levelNum, score, onContinue, isLastLevel }) {
  const level = LEVEL_DATA[levelNum - 1];
  const pct   = Math.round((levelNum / TOTAL_LEVELS) * 100);
  const bar   = Array.from({ length: 20 }, (_, i) =>
    i < Math.round(pct / 5) ? '█' : '░'
  ).join('');
  return (
    <div className="opuw-levelcomplete">
      <div className="opuw-lc-header">
        {isLastLevel ? '▓▓▓ SIGNAL LIBERATED ▓▓▓' : '▓ NODE CLEARED ▓'}
      </div>
      <div className="opuw-lc-node">{level.id} — {level.name}</div>
      <div className="opuw-lc-region">{level.region}</div>
      <div className="opuw-lc-score">
        <span className="opuw-lc-score-label">STRIKE SCORE</span>
        <span className="opuw-lc-score-value">{score}</span>
      </div>
      <div className="opuw-lc-signal">
        <div className="opuw-lc-signal-label">
          OPU-WEB CALIBRATION: {100 - pct}% REMAINING
        </div>
        <div className="opuw-lc-signal-bar">[{bar}] {pct}%</div>
        {isLastLevel && <div className="opuw-lc-signal-clear">CALIBRATION COMPLETE</div>}
      </div>
      {isLastLevel ? (
        <div className="opuw-lc-final-msg">
          <p>Good job!</p>
        </div>
      ) : (
        // CHANGED: progress messages calibrated to 20-level structure
        <div className="opuw-lc-progress-msg">
          {levelNum < 8  && 'Signal path fragmentary — keep clearing nodes.'}
          {levelNum >= 8  && levelNum < 14 && 'Suppression weakening — his signal is getting louder.'}
          {levelNum >= 14 && levelNum < 20 && 'Core layer breach imminent. Almost there.'}
        </div>
      )}
      <button className="opuw-btn-primary" onClick={onContinue}>
        {isLastLevel ? 'OPEN OPU-WEB MESSAGE SYSTEM' : 'CONTINUE MISSION'}
      </button>
    </div>
  );
}

// ─── Postgame ─────────────────────────────────────────────────────────────────
function PostgameScreen({ sessionId, totalScore, confirmedKills, onReset }) {
  const [messagesUsed, setMessagesUsed] = useState(0);
  return (
    <div className="opuw-postgame">
      <div className="opuw-postgame-header">
        <div className="opuw-postgame-title">▓▓▓ OPU-WEB — SIGNAL LIBERATED ▓▓▓</div>
        <div className="opuw-postgame-subtitle">
          {/* CHANGED: 30 → 20 */}
          ALL 20 NODES CLEARED — CHAT SYSTEM ACTIVE
        </div>
      </div>
      <div className="opuw-postgame-stats">
        <div className="opuw-postgame-stat">
          <span className="opuw-postgame-stat-label">TOTAL SCORE</span>
          <span className="opuw-postgame-stat-value">{totalScore.toLocaleString()}</span>
        </div>
        <div className="opuw-postgame-stat">
          <span className="opuw-postgame-stat-label">CONFIRMED KILLS</span>
          <span className="opuw-postgame-stat-value">{confirmedKills.toLocaleString()}</span>
        </div>
        <div className="opuw-postgame-stat">
          <span className="opuw-postgame-stat-label">NODES CLEARED</span>
          {/* CHANGED: 30 → 20 */}
          <span className="opuw-postgame-stat-value">20 / 20</span>
        </div>
      </div>
      <div className="opuw-postgame-divider" />
      <div className="opuw-postgame-transmission">
        <div className="opuw-postgame-transmission-label">▶ INCOMING TRANSMISSION — OPU-1103</div>
        <div className="opuw-postgame-transmission-text">
          You have to help us.... you have to help us... please you have to get us out of here....
        </div>
      </div>
      <div className="opuw-postgame-divider" />
      <VeronicaChat
        level={20}                                       /* CHANGED: 30 → 20 */
        mode="postgame"
        messagesUsed={messagesUsed}
        onMessageSent={() => setMessagesUsed(n => n + 1)}
        sessionId={sessionId}
      />
      <div className="opuw-postgame-divider" />
      <div className="opuw-postgame-actions">
        <button className="opuw-btn-secondary" onClick={onReset}>↺ RESET PROGRESS & REPLAY</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
export default function OPUVeronica() {
  const [progress,      setProgress]      = useState(() => loadProgress() || defaultProgress());
  const [view,          setView]          = useState('map');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [lastScore,     setLastScore]     = useState(0);
  const [messagesUsed,  setMessagesUsed]  = useState(0);

  const sessionIdRef = useRef(null);
  const getSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      const stored = sessionStorage.getItem('veronica_opuweb_session');
      if (stored) {
        sessionIdRef.current = stored;
      } else {
        sessionIdRef.current = `vro_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem('veronica_opuweb_session', sessionIdRef.current);
      }
    }
    return sessionIdRef.current;
  }, []);

  useEffect(() => { saveProgress(progress); }, [progress]);
  useEffect(() => { setMessagesUsed(0); }, [selectedLevel]);

  const handleNodeClick = useCallback((levelNum) => {
    setSelectedLevel(levelNum);
    setMessagesUsed(0);
    setView('playing');
  }, []);

  const handleGameComplete = useCallback((score) => {
    const levelNum = selectedLevel;
    const isLast   = levelNum === TOTAL_LEVELS;
    setLastScore(score);
    setProgress(prev => {
      const alreadyDone  = prev.completedLevels.includes(levelNum);
      const newCompleted = alreadyDone
        ? prev.completedLevels
        : [...prev.completedLevels, levelNum];
      const nextLevel = Math.min(Math.max(prev.currentLevel, levelNum + 1), TOTAL_LEVELS);
      return {
        ...prev,
        completedLevels:  newCompleted,
        currentLevel:     nextLevel,
        totalScore:       prev.totalScore + score,
        confirmedKills:   prev.confirmedKills + score,
        postgameUnlocked: isLast ? true : prev.postgameUnlocked,
      };
    });
    setView('levelcomplete');
  }, [selectedLevel]);

  const handleLevelContinue = useCallback(() => {
    if (selectedLevel === TOTAL_LEVELS) { setView('postgame'); }
    else { setView('map'); setSelectedLevel(null); }
  }, [selectedLevel]);

  const handleCancel = useCallback(() => {
    setView('map');
    setSelectedLevel(null);
  }, []);

  const handleReset = useCallback(() => {
    const fresh = defaultProgress();
    setProgress(fresh);
    saveProgress(fresh);
    sessionStorage.removeItem('veronica_opuweb_session');
    sessionIdRef.current = null;
    setView('map');
    setSelectedLevel(null);
    setLastScore(0);
    setMessagesUsed(0);
  }, []);

  // ── POSTGAME ─────────────────────────────────────────────────────────────────
  if (view === 'postgame') {
    return (
      <PostgameScreen
        sessionId={getSessionId()}
        totalScore={progress.totalScore}
        confirmedKills={progress.confirmedKills}
        onReset={handleReset}
      />
    );
  }

  // ── PLAYING ──────────────────────────────────────────────────────────────────
  if (view === 'playing' && selectedLevel !== null) {
    const levelData = LEVEL_DATA[selectedLevel - 1];
    return (
      <div className="opuw-root">
        <DroneGame
          missionId={levelData.id}
          missionTitle={levelData.name}
          onComplete={handleGameComplete}
          onCancel={handleCancel}
          confirmedKills={progress.confirmedKills}
        />
      </div>
    );
  }

  // ── LEVEL COMPLETE ───────────────────────────────────────────────────────────
  if (view === 'levelcomplete' && selectedLevel !== null) {
    return (
      <div className="opuw-root">
        <div className="opuw-levelcomplete-layout">
          <LevelComplete
            levelNum={selectedLevel} score={lastScore}
            onContinue={handleLevelContinue}
            isLastLevel={selectedLevel === TOTAL_LEVELS}
          />
          <div className="opuw-levelcomplete-chat">
            <div className="opuw-levelcomplete-chat-label">
              ▶ DEBRIEF WITH VERONICA
            </div>
            <VeronicaChat
              level={selectedLevel}
              mode="level"
              messagesUsed={messagesUsed}
              onMessageSent={() => setMessagesUsed(n => n + 1)}
              sessionId={getSessionId()}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── MAP VIEW ─────────────────────────────────────────────────────────────────
  const { currentLevel, completedLevels, totalScore, confirmedKills } = progress;
  const getNodeStatus = (n) =>
    completedLevels.includes(n) ? 'complete' : n <= currentLevel ? 'available' : 'locked';

  // CHANGED: country-based sectors replacing alpha/bravo/etc. — 4 nodes each, Pakistan last
  const sectors = [
    { name: 'UNITED STATES', levels: [1,  2,  3,  4]  },
    { name: 'EUROPE',        levels: [5,  6,  7,  8]  },
    { name: 'RUSSIA',        levels: [9,  10, 11, 12] },
    { name: 'CHINA',         levels: [13, 14, 15, 16] },
    { name: 'PAKISTAN',      levels: [17, 18, 19, 20] },
  ];

  const completionPct = Math.round((completedLevels.length / TOTAL_LEVELS) * 100);
  // CHANGED: 30 → 20 bar segments
  const signalBar     = Array.from({ length: 20 }, (_, i) =>
    i < completedLevels.length ? '█' : '░'
  ).join('');

  return (
    <div className="opuw-root">
      <div className="opuw-map">
        <div className="opuw-map-header">
          <div className="opuw-map-portrait-row">
            <div className="opuw-map-portrait">
              <div className="opuw-map-portrait-frame">
                <div className="opuw-map-portrait-img">
                  <img
                    src="/images/veronica-portrait.jpg"
                    alt="Veronica Rodriguez"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <div className="opuw-map-portrait-scanlines" />
                </div>
                <div className="opuw-map-portrait-label">VERONICA RODRIGUEZ</div>
                <div className="opuw-map-portrait-sublabel">OPU-WEB CALIBRATION ASSISTANT</div>
              </div>
            </div>
            <div className="opuw-map-header-text">
              <div className="opuw-map-title">▓▓▓ OPU-WEB CALIBRATION ▓▓▓</div>
              <div className="opuw-map-subtitle">
                OPHIDIA GLOBAL — 5 COUNTRIES · 20 NODES
              </div>
              <div className="opuw-map-lore">
                {/* CHANGED: lore reflects 20 nodes across real countries */}
                Veronica has mapped 20 nodes across five nations.
                Clear each in sequence to complete the OPU-WEB calibration.
              </div>
            </div>
          </div>
        </div>
        <div className="opuw-map-signal">
          <div className="opuw-map-signal-label">
            CALIBRATION UPDATE: {completionPct}% CLEARED
            {completionPct === 100 && ' — TRANSMISSION ACTIVE'}
          </div>
          <div className="opuw-map-signal-bar">[{signalBar}]</div>
        </div>
        <div className="opuw-map-stats">
          <div className="opuw-map-stat">
            <span className="opuw-map-stat-label">NODES CLEARED</span>
            <span className="opuw-map-stat-value">{completedLevels.length} / {TOTAL_LEVELS}</span>
          </div>
          <div className="opuw-map-stat">
            <span className="opuw-map-stat-label">TOTAL SCORE</span>
            <span className="opuw-map-stat-value">{totalScore.toLocaleString()}</span>
          </div>
          <div className="opuw-map-stat">
            <span className="opuw-map-stat-label">CONFIRMED KILLS</span>
            <span className="opuw-map-stat-value">{confirmedKills.toLocaleString()}</span>
          </div>
          <div className="opuw-map-stat">
            <span className="opuw-map-stat-label">ACTIVE NODE</span>
            <span className="opuw-map-stat-value" style={{ color: '#ffaa00' }}>
              {LEVEL_DATA[currentLevel - 1]?.id ?? 'COMPLETE'}
            </span>
          </div>
        </div>
        <div className="opuw-sectors">
          {sectors.map(sector => (
            <div key={sector.name} className="opuw-sector">
              <div className="opuw-sector-label">{sector.name}</div>
              <div className="opuw-sector-nodes">
                {sector.levels.map(n => (
                  <NetworkNode
                    key={n} levelNum={n}
                    status={getNodeStatus(n)}
                    isCurrent={n === currentLevel}
                    onClick={handleNodeClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        {progress.postgameUnlocked && (
          <div className="opuw-map-postgame-banner">
            <div className="opuw-map-postgame-label">
              ▓ OPU-1103 SIGNAL ACTIVE — VERONICA CHANNEL OPEN ▓
            </div>
            <button className="opuw-btn-primary" onClick={() => setView('postgame')}>
              OPEN CHANNEL
            </button>
          </div>
        )}
        <div className="opuw-map-instructions">
          <span>Click any available node to begin strike operation calibrations.</span>
          {/* CHANGED: 30 → 20 */}
          <span>Complete all 20 nodes to complete the calibration.</span>
        </div>
        {completedLevels.length > 0 && (
          <button className="opuw-btn-reset" onClick={handleReset}>↺ RESET PROGRESS</button>
        )}
      </div>
    </div>
  );
}